import { cn } from '@/lib/utils'
import { ArrowRight } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { ReactNode } from 'react'

type PremiumHeroProps = {
  eyebrow?: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  image: string
  imageAlt?: string
  children?: ReactNode
  align?: 'left' | 'center'
}

export function PremiumHero({
  eyebrow,
  title,
  subtitle,
  image,
  imageAlt,
  children,
  align = 'left',
}: PremiumHeroProps) {
  return (
    <section className="overflow-hidden bg-background pb-6 pt-24 text-foreground sm:pb-8 sm:pt-28 lg:pt-28">
      <div
        className={cn(
          'site-shell grid items-center gap-7 lg:grid-cols-[0.9fr_1.1fr] lg:gap-10',
          align === 'center' && 'lg:grid-cols-1',
        )}
      >
        <div className={cn('py-2 lg:py-4', align === 'center' && 'mx-auto max-w-4xl text-center')}>
          {eyebrow && (
            <div className="site-label mb-4">
              {eyebrow}
            </div>
          )}
          <h1 className="font-sans text-[clamp(2.65rem,6vw,5.75rem)] font-semibold leading-[0.94] tracking-[-0.055em] text-foreground">
            {title}
          </h1>
          {subtitle && (
            <p className={cn('mt-5 max-w-2xl text-base leading-7 text-muted-foreground md:text-lg md:leading-8', align === 'center' && 'mx-auto')}>
              {subtitle}
            </p>
          )}
          {children && <div className="mt-6">{children}</div>}
        </div>

        <div className={cn('relative min-h-[300px] overflow-hidden rounded-[1.5rem] bg-primary sm:min-h-[400px] lg:min-h-[500px]', align === 'center' && 'lg:min-h-[460px]')}>
          <Image
            src={image}
            alt={imageAlt ?? (typeof title === 'string' ? title : 'Oorva experience')}
            fill
            priority
            loading="eager"
            fetchPriority="high"
            quality={75}
            className="image-drift object-cover"
            sizes={align === 'center' ? '100vw' : '(min-width: 1024px) 56vw, 100vw'}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary/45 via-transparent to-transparent" />
        </div>
      </div>
    </section>
  )
}

type PremiumSectionProps = {
  children: ReactNode
  className?: string
  id?: string
  tone?: 'ivory' | 'forest'
}

export function PremiumSection({
  children,
  className,
  id,
  tone = 'ivory',
}: PremiumSectionProps) {
  return (
    <section
      id={id}
      className={cn(
        'relative overflow-hidden',
        tone === 'ivory'
          ? 'bg-background text-foreground'
          : 'border-y border-white/10 bg-primary text-primary-foreground',
      )}
    >
      {tone === 'forest' && <div className="absolute inset-0 premium-grain opacity-35" />}
      <div className={cn('site-shell site-section relative z-[1]', className)}>
        {children}
      </div>
    </section>
  )
}

export function PremiumEyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('site-label', className)}>
      {children}
    </div>
  )
}

export function PremiumHeading({
  children,
  className,
  size = 'default',
}: {
  children: ReactNode
  className?: string
  size?: 'default' | 'compact'
}) {
  return (
    <h2
      className={cn(
        size === 'default'
          ? 'site-title'
          : 'font-sans text-[2rem] font-semibold leading-[1.02] tracking-[-0.04em] sm:text-[2.35rem] md:text-[2.75rem]',
        className,
      )}
    >
      {children}
    </h2>
  )
}

export function PremiumIntro({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn('site-copy max-w-2xl md:text-[1.05rem]', className)}>{children}</p>
}

export function PremiumButton({
  href,
  children,
  external,
}: {
  href: string
  children: ReactNode
  external?: boolean
}) {
  return (
    <Link
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className="site-button-primary"
    >
      {children}
      <ArrowRight className="h-4 w-4" />
    </Link>
  )
}

export function PremiumImageCard({
  image,
  title,
  label,
  body,
  href,
  external,
  cta,
  analytics,
}: {
  image: string
  title: string
  label?: string
  body?: string
  href?: string
  external?: boolean
  cta?: string
  analytics?: {
    experienceSlug: string
    destination: string
    theme?: string
    selectionSource: string
  }
}) {
  const content = (
    <div className={cn(
      'relative min-h-[280px] overflow-hidden rounded-[1.5rem] border border-border bg-card md:min-h-[320px]',
      'travel-card group',
    )}>
      <Image
        src={image}
        alt={title}
        fill
        loading="lazy"
        quality={75}
        className="image-lift object-cover"
        sizes="(min-width: 1024px) 25vw, (min-width: 768px) 50vw, 100vw"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/10 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-5 text-white md:p-6">
        {label && (
          <div className="mb-2 inline-flex rounded-full border border-white/20 bg-primary/72 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-brand-highlight backdrop-blur">
            {label}
          </div>
        )}
        <h3 className="font-sans text-[1.55rem] font-semibold leading-[1.05] tracking-[-0.025em]">{title}</h3>
        {body && <p className="mt-2 max-w-sm text-sm leading-6 text-white/78">{body}</p>}
        {href && cta && (
          <p className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-white">
            {cta} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </p>
        )}
      </div>
    </div>
  )

  if (!href) return content
  const isExternal = external ?? href.startsWith('http')

  return (
    <Link
      href={href}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      data-analytics-event={analytics ? 'experience_selected' : undefined}
      data-analytics-experience-slug={analytics?.experienceSlug}
      data-analytics-experience-name={analytics ? title : undefined}
      data-analytics-destination={analytics?.destination}
      data-analytics-theme={analytics?.theme}
      data-analytics-selection-source={analytics?.selectionSource}
      className="block"
    >
      {content}
    </Link>
  )
}

export function PremiumStat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div className="font-sans text-3xl font-semibold leading-none tracking-[-0.025em] text-brand md:text-4xl">{value}</div>
      <div className="mt-2 text-[0.7rem] font-semibold uppercase leading-5 tracking-[0.12em] opacity-55">{label}</div>
    </div>
  )
}

export function PremiumFeature({
  icon,
  title,
  body,
  dark = false,
}: {
  icon: ReactNode
  title: string
  body: string
  dark?: boolean
}) {
  return (
    <div
      className={cn(
        'rounded-[1.5rem] border p-5',
        dark ? 'border-white/10 bg-white/[0.055]' : 'border-border bg-card',
      )}
    >
      <div className={cn('mb-3 flex h-10 w-10 items-center justify-center rounded-full', dark ? 'bg-white/10 text-brand-highlight' : 'bg-brand-soft text-brand')}>
        {icon}
      </div>
      <h3 className="font-sans text-base font-semibold leading-6 tracking-[-0.01em]">{title}</h3>
      <p className={cn('mt-2 text-sm leading-6', dark ? 'text-white/65' : 'text-muted-foreground')}>{body}</p>
    </div>
  )
}
